import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { apiRequest } from '../utils/api';
import {
  AdminRecommendedListing,
  AdminRecommendedListingsResponse,
  AdminRecommendedListingsSearchResponse,
} from '../RecommendedProperties/types';

interface AdminRecommendedPropertiesToolProps {
  isLoggedIn: boolean;
  isAdmin: boolean;
  isMobile: boolean;
  show: boolean;
  setShow: (visible: boolean) => void;
}

const previewImageStyle: React.CSSProperties = {
  width: 96,
  height: 72,
  objectFit: 'cover',
  borderRadius: 8,
  flexShrink: 0,
  backgroundColor: '#f5f5f5',
};

const scrollPaneStyle: React.CSSProperties = {
  maxHeight: '60vh',
  overflowY: 'auto',
};

const AdminRecommendedPropertiesTool: React.FC<AdminRecommendedPropertiesToolProps> = ({
  isLoggedIn,
  isAdmin,
  isMobile,
  show,
  setShow,
}) => {
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [busyListingId, setBusyListingId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [curatedListings, setCuratedListings] = useState<AdminRecommendedListing[]>([]);
  const [unresolvedListingIds, setUnresolvedListingIds] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<AdminRecommendedListing[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isFallbackConfig, setIsFallbackConfig] = useState(false);

  const curatedIdSet = useMemo(
    () => new Set([
      ...curatedListings.map((listing) => listing._id),
      ...unresolvedListingIds,
    ]),
    [curatedListings, unresolvedListingIds],
  );

  const closeModal = useCallback(() => {
    setShow(false);
    setError(null);
    setSuccess(null);
  }, [setShow]);

  const fetchCuratedListings = useCallback(async () => {
    if (!isLoggedIn || !isAdmin) return;

    try {
      setIsLoadingList(true);
      setError(null);

      const response = await apiRequest(
        '/api/admin/recommended-listings',
        { method: 'GET' },
      ) as AdminRecommendedListingsResponse;

      setCuratedListings(response.listings || []);
      setUnresolvedListingIds(response.unresolved_listing_ids || []);
      setIsFallbackConfig(Boolean(response.is_fallback));
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to load recommended properties');
      setCuratedListings([]);
      setUnresolvedListingIds([]);
      setIsFallbackConfig(false);
    } finally {
      setIsLoadingList(false);
    }
  }, [isLoggedIn, isAdmin]);

  const runSearch = useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!isLoggedIn || !isAdmin) return;

    if (!trimmedQuery) {
      setLastSearchQuery('');
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setError(null);
      setLastSearchQuery(trimmedQuery);

      const params = new URLSearchParams({
        q: trimmedQuery,
        limit: '12',
      });

      const response = await apiRequest(
        `/api/admin/recommended-listings/search?${params.toString()}`,
        { method: 'GET' },
      ) as AdminRecommendedListingsSearchResponse;

      setSearchResults(response.results || []);
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to search listings');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [isLoggedIn, isAdmin]);

  const refreshSearchResults = useCallback(async () => {
    if (!lastSearchQuery) return;
    await runSearch(lastSearchQuery);
  }, [lastSearchQuery, runSearch]);

  useEffect(() => {
    if (!show) return;

    if (!isLoggedIn || !isAdmin || isMobile) {
      setShow(false);
      return;
    }

    fetchCuratedListings();
  }, [fetchCuratedListings, isAdmin, isLoggedIn, isMobile, setShow, show]);

  const handleAddListing = useCallback(async (listingId: string) => {
    try {
      setBusyListingId(listingId);
      setError(null);
      setSuccess(null);

      const response = await apiRequest(
        '/api/admin/recommended-listings',
        {
          method: 'POST',
          body: { listing_id: listingId },
        },
      ) as { already_recommended?: boolean };

      setSuccess(
        response.already_recommended
          ? 'Listing is already in the recommended properties list.'
          : 'Listing added to recommended properties.',
      );

      await fetchCuratedListings();
      await refreshSearchResults();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to add listing');
    } finally {
      setBusyListingId(null);
    }
  }, [fetchCuratedListings, refreshSearchResults]);

  const handleRemoveListing = useCallback(async (listingId: string) => {
    if (!window.confirm('Remove this listing from recommended properties?')) {
      return;
    }

    try {
      setBusyListingId(listingId);
      setError(null);
      setSuccess(null);

      await apiRequest(
        `/api/admin/recommended-listings/${listingId}`,
        { method: 'DELETE' },
      );

      setSuccess('Listing removed from recommended properties.');
      await fetchCuratedListings();
      await refreshSearchResults();
    } catch (err: any) {
      setError(err?.error || err?.message || 'Failed to remove listing');
    } finally {
      setBusyListingId(null);
    }
  }, [fetchCuratedListings, refreshSearchResults]);

  const handleSearchSubmit = useCallback(async (event?: React.FormEvent) => {
    event?.preventDefault();
    await runSearch(searchInput);
  }, [runSearch, searchInput]);

  const renderListingMeta = (listing: AdminRecommendedListing) => {
    const parts = [
      listing.property_size ? `${listing.property_size} m²` : null,
      listing.property_rooms ? `${listing.property_rooms} rooms` : null,
      listing.property_floor ? `Floor ${listing.property_floor}` : null,
    ].filter(Boolean);

    return parts.join(' • ');
  };

  const renderAddress = (listing: AdminRecommendedListing) => {
    const houseNumber = listing.address?.houseNumber && listing.address.houseNumber !== 'Unknown'
      ? ` ${listing.address.houseNumber}`
      : '';
    return `${listing.address?.street || 'Unknown address'}${houseNumber}, ${listing.address?.city || ''}`.trim();
  };

  return (
    <Modal
      show={show && isLoggedIn && isAdmin && !isMobile}
      onHide={closeModal}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Admin: Recommended properties</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          {isFallbackConfig && <Badge variant="warning" className="text-dark">Using fallback seed</Badge>}
          <Badge variant="light" className="text-dark">
            Curated: {curatedListings.length + unresolvedListingIds.length}
          </Badge>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 className="mb-0">Current curated list</h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={fetchCuratedListings}
                disabled={isLoadingList}
              >
                {isLoadingList ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>

            <div style={scrollPaneStyle}>
              {isLoadingList && curatedListings.length === 0 && unresolvedListingIds.length === 0 ? (
                <div className="py-4 text-center">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <ListGroup>
                  {curatedListings.map((listing) => (
                    <ListGroup.Item
                      key={listing._id}
                      className="d-flex align-items-start gap-3"
                    >
                      {listing.images && listing.images.length > 0 ? (
                        <img
                          src={listing.images[0].thumbnailUrl || listing.images[0].url}
                          alt={renderAddress(listing)}
                          style={previewImageStyle}
                        />
                      ) : (
                        <div style={previewImageStyle} />
                      )}

                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <strong>{renderAddress(listing)}</strong>
                          {listing.recommended_position && (
                            <Badge variant="secondary">#{listing.recommended_position}</Badge>
                          )}
                        </div>
                        <div className="text-muted small mb-1">
                          Listing ID: {listing._id}
                        </div>
                        <div className="text-muted small mb-1">
                          Building ID: {listing.building_id}
                        </div>
                        {renderListingMeta(listing) && (
                          <div className="small mb-1">{renderListingMeta(listing)}</div>
                        )}
                        {listing.property_description && (
                          <div className="small text-muted">{listing.property_description}</div>
                        )}
                      </div>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveListing(listing._id)}
                        disabled={busyListingId === listing._id}
                      >
                        {busyListingId === listing._id ? 'Removing...' : 'Remove'}
                      </Button>
                    </ListGroup.Item>
                  ))}

                  {unresolvedListingIds.map((listingId) => (
                    <ListGroup.Item
                      key={`unresolved-${listingId}`}
                      className="d-flex align-items-center justify-content-between gap-3"
                    >
                      <div>
                        <div className="fw-semibold">Unavailable listing</div>
                        <div className="small text-muted">{listingId}</div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveListing(listingId)}
                        disabled={busyListingId === listingId}
                      >
                        {busyListingId === listingId ? 'Removing...' : 'Remove'}
                      </Button>
                    </ListGroup.Item>
                  ))}

                  {!isLoadingList && curatedListings.length === 0 && unresolvedListingIds.length === 0 && (
                    <ListGroup.Item className="text-muted">
                      No curated listings yet.
                    </ListGroup.Item>
                  )}
                </ListGroup>
              )}
            </div>
          </Col>

          <Col lg={6}>
            <h5 className="mb-2">Search sell listings to add</h5>
            <Form onSubmit={handleSearchSubmit}>
              <InputGroup className="mb-3">
                <Form.Control
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search by listing ID, building ID, street, city..."
                />
                <Button type="submit" variant="primary" disabled={isSearching}>
                  {isSearching ? 'Searching...' : 'Search'}
                </Button>
              </InputGroup>
            </Form>

            <div style={scrollPaneStyle}>
              {isSearching && searchResults.length === 0 ? (
                <div className="py-4 text-center">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <ListGroup>
                  {searchResults.map((listing) => {
                    const isSelected = curatedIdSet.has(listing._id);
                    return (
                      <ListGroup.Item
                        key={`search-${listing._id}`}
                        className="d-flex align-items-start gap-3"
                      >
                        {listing.images && listing.images.length > 0 ? (
                          <img
                            src={listing.images[0].thumbnailUrl || listing.images[0].url}
                            alt={renderAddress(listing)}
                            style={previewImageStyle}
                          />
                        ) : (
                          <div style={previewImageStyle} />
                        )}

                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <strong>{renderAddress(listing)}</strong>
                            {isSelected && <Badge variant="success">Selected</Badge>}
                          </div>
                          <div className="text-muted small mb-1">
                            Listing ID: {listing._id}
                          </div>
                          <div className="text-muted small mb-1">
                            Building ID: {listing.building_id}
                          </div>
                          {renderListingMeta(listing) && (
                            <div className="small mb-1">{renderListingMeta(listing)}</div>
                          )}
                          {listing.property_description && (
                            <div className="small text-muted">{listing.property_description}</div>
                          )}
                        </div>

                        <Button
                          variant={isSelected ? 'outline-secondary' : 'outline-primary'}
                          size="sm"
                          onClick={() => handleAddListing(listing._id)}
                          disabled={isSelected || busyListingId === listing._id}
                        >
                          {busyListingId === listing._id
                            ? 'Saving...'
                            : isSelected
                              ? 'Added'
                              : 'Add'}
                        </Button>
                      </ListGroup.Item>
                    );
                  })}

                  {!isSearching && lastSearchQuery && searchResults.length === 0 && (
                    <ListGroup.Item className="text-muted">
                      No eligible sell listings matched "{lastSearchQuery}".
                    </ListGroup.Item>
                  )}

                  {!lastSearchQuery && (
                    <ListGroup.Item className="text-muted">
                      Search to find listings and add them to the curated list.
                    </ListGroup.Item>
                  )}
                </ListGroup>
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default AdminRecommendedPropertiesTool;
